import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Bell, Check, RefreshCcw, FileText, AlertTriangle, Clock, UserCircle2, Inbox, Calendar, MessageSquare, AtSign } from 'lucide-react';
import { toast } from 'sonner';
import { log } from '@/lib/log';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_META = {
    access_request: { label: 'Demande d\'accès', icon: UserCircle2, tone: 'text-blue-700 bg-blue-50' },
    meeting_started: { label: 'Réunion démarrée', icon: Calendar, tone: 'text-purple-700 bg-purple-50' },
    approval_requested: { label: 'Validation requise', icon: Check, tone: 'text-amber-700 bg-amber-50' },
    approval_pending: { label: 'Validation en attente', icon: Check, tone: 'text-amber-700 bg-amber-50' },
    invoice_due: { label: 'Facture à payer', icon: Clock, tone: 'text-orange-700 bg-orange-50' },
    invoice_overdue: { label: 'Facture en retard', icon: AlertTriangle, tone: 'text-red-700 bg-red-50' },
    mention: { label: '@Mention', icon: AtSign, tone: 'text-primary bg-primary-light' },
    donation_received: { label: 'Don reçu', icon: FileText, tone: 'text-emerald-700 bg-emerald-50' },
    contact_message: { label: 'Message contact', icon: MessageSquare, tone: 'text-sky-700 bg-sky-50' },
    system: { label: 'Système', icon: Bell, tone: 'text-stone-700 bg-stone-100' },
};

const FILTERS = [
    { key: 'all', label: 'Tout' },
    { key: 'unread', label: 'Non lues' },
    { key: 'mention', label: '@Mentions' },
    { key: 'approval_pending', label: 'Validations' },
    { key: 'invoice_due', label: 'Factures à payer' },
    { key: 'invoice_overdue', label: 'En retard' },
    { key: 'meeting_started', label: 'Réunions' },
];

export default function Notifications() {
    const { session } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [filter, setFilter] = useState('all');

    const authHeader = useCallback(() => session?.access_token
        ? { headers: { Authorization: `Bearer ${session.access_token}` } }
        : null, [session]);

    const load = useCallback(async () => {
        const h = authHeader();
        if (!h) return;
        setLoading(true);
        try {
            const r = await axios.get(`${API}/notifications?limit=200`, h);
            setItems(r.data.items || []);
        } catch (e) {
            toast.error('Chargement des notifications échoué');
        } finally {
            setLoading(false);
        }
    }, [authHeader]);

    useEffect(() => { load(); }, [load]);

    const handleScan = async () => {
        const h = authHeader();
        if (!h) return;
        setScanning(true);
        try {
            const r = await axios.post(`${API}/notifications/scan`, {}, h);
            const created = r.data.created || 0;
            toast.success(created > 0
                ? `${created} nouvelle${created > 1 ? 's' : ''} notification${created > 1 ? 's' : ''} générée${created > 1 ? 's' : ''}`
                : 'Tout est à jour — aucune action en attente');
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Scan échoué');
        } finally {
            setScanning(false);
        }
    };

    const markAllRead = async () => {
        const h = authHeader();
        if (!h) return;
        try {
            await axios.post(`${API}/notifications/mark-all-read`, {}, h);
            setItems(prev => prev.map(it => ({ ...it, is_read: true })));
            toast.success('Toutes marquées lues');
        } catch (e) { toast.error('Action échouée'); }
    };

    const openNotif = async (n) => {
        const h = authHeader();
        if (h && !n.is_read) {
            try {
                await axios.post(`${API}/notifications/${n.id}/read`, {}, h);
            } catch (e) {
                // Optimistic UI: mark-as-read may fail silently (e.g. already read elsewhere);
                // we still update local state below for responsiveness.
                log.warn('[notifications] mark-as-read failed:', e?.response?.status || e?.message);
            }
            setItems(prev => prev.map(it => it.id === n.id ? { ...it, is_read: true } : it));
        }
        if (n.link) navigate(n.link);
    };

    const filtered = useMemo(() => {
        if (filter === 'all') return items;
        if (filter === 'unread') return items.filter(i => !i.is_read);
        return items.filter(i => i.type === filter);
    }, [items, filter]);

    const counts = useMemo(() => {
        const c = { all: items.length, unread: items.filter(i => !i.is_read).length };
        ['approval_pending', 'invoice_due', 'invoice_overdue', 'meeting_started'].forEach(t => {
            c[t] = items.filter(i => i.type === t).length;
        });
        return c;
    }, [items]);

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="notifications-page">
            <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">Centre</div>
                    <h1 className="font-display text-3xl sm:text-4xl text-ink">Notifications</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Validations en attente, factures à payer, réunions et activité de vos espaces.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleScan}
                        disabled={scanning}
                        data-testid="notif-scan-btn"
                        className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-white hover:bg-surface-muted text-sm transition-colors disabled:opacity-60"
                    >
                        <RefreshCcw size={14} className={scanning ? 'animate-spin' : ''} />
                        {scanning ? 'Scan…' : 'Scanner maintenant'}
                    </button>
                    {counts.unread > 0 && (
                        <button
                            type="button"
                            onClick={markAllRead}
                            data-testid="notif-mark-all-read-btn"
                            className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-primary text-primary-foreground hover:opacity-90 text-sm"
                        >
                            <Check size={14} /> Tout marquer lu
                        </button>
                    )}
                </div>
            </header>

            <div className="flex flex-wrap gap-2 mb-6" data-testid="notif-filters">
                {FILTERS.map(f => {
                    const n = counts[f.key];
                    const active = filter === f.key;
                    return (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => setFilter(f.key)}
                            data-testid={`notif-filter-${f.key}`}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                                active
                                    ? 'bg-ink text-white border-ink'
                                    : 'bg-white text-muted-foreground border-border hover:border-ink/40'
                            }`}
                        >
                            {f.label}
                            {typeof n === 'number' && n > 0 && (
                                <span className={`ml-1.5 text-[10px] ${active ? 'opacity-80' : 'text-ink/70'}`}>· {n}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {loading ? (
                <div className="text-center text-muted-foreground py-16">Chargement…</div>
            ) : filtered.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-xl bg-surface-muted/40" data-testid="notif-empty">
                    <Inbox className="mx-auto mb-3 text-muted-foreground" size={28} />
                    <p className="text-muted-foreground text-sm">
                        {filter === 'all'
                            ? 'Aucune notification pour le moment.'
                            : 'Aucune notification correspondant à ce filtre.'}
                    </p>
                    {filter === 'all' && (
                        <button
                            type="button"
                            onClick={handleScan}
                            className="mt-4 text-xs text-primary hover:underline"
                            data-testid="notif-empty-scan"
                        >
                            Lancer un scan
                        </button>
                    )}
                </div>
            ) : (
                <ul className="space-y-2" data-testid="notif-list">
                    {filtered.map(n => {
                        const meta = TYPE_META[n.type] || TYPE_META.system;
                        const Icon = meta.icon;
                        return (
                            <li key={n.id} data-testid={`notif-row-${n.id}`}>
                                <button
                                    type="button"
                                    onClick={() => openNotif(n)}
                                    className={`w-full text-left flex gap-3 items-start p-4 rounded-xl border transition-colors ${
                                        n.is_read
                                            ? 'bg-white border-border hover:border-ink/30'
                                            : 'bg-primary-light/30 border-primary/20 hover:border-primary/50'
                                    }`}
                                >
                                    <span className={`shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${meta.tone}`}>
                                        <Icon size={16} />
                                    </span>
                                    <span className="flex-1 min-w-0">
                                        <span className="flex items-center justify-between gap-2">
                                            <span className="font-medium text-sm text-ink truncate">{n.title}</span>
                                            {!n.is_read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                                        </span>
                                        {n.body && (
                                            <span className="block text-xs text-muted-foreground mt-1 line-clamp-2">{n.body}</span>
                                        )}
                                        <span className="block text-[10px] uppercase tracking-wider text-muted-foreground/70 mt-2">
                                            {meta.label} · {new Date(n.created_at).toLocaleString('fr-FR')}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
